import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { TenantMemberSelector } from 'apps/TenantApp/components/MemberSelector/TenantMemberSelector';
import { OverlayProps } from 'components/TaskOverlay';
import React, { useEffect, useState } from 'react';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { CreateVaultModal_createVault_Mutation } from './__generated__/CreateVaultModal_createVault_Mutation.graphql';
import { generatePath } from 'react-router-dom';
import { VAULTS_MANAGER_VAULT_PATH } from 'utils/routes';
import { CreateVaultModal_vaultManager$key } from './__generated__/CreateVaultModal_vaultManager.graphql';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { useFeatureFlag } from 'utils/sessionProvider';
import {
  VaultManagementGroupOption,
  VaultManagementGroupSelect,
} from 'apps/TenantApp/screens/Vaults/components/VaultManagementGroupSelect';
import { notEmpty } from 'utils/NotEmptyFilter';

const vaultManagerFragment = graphql`
  fragment CreateVaultModal_vaultManager on VaultManager {
    __typename
    id
    vaults {
      edges {
        node {
          id
          members {
            id
            name
          }
        }
      }
    }
    vaultManagementGroups(managedByMyself: true) {
      edges {
        node {
          id
        }
      }
    }
    ...VaultManagementGroupSelect_vaultManager
  }
`;

const createVaultMutation = graphql`
  mutation CreateVaultModal_createVault_Mutation(
    $memberIds: [ID!]!
    $name: String!
    $vaultManagementGroupId: ID
    $connections: [ID!]!
  ) {
    createVault(
      input: {
        memberIds: $memberIds
        name: $name
        vaultManagementGroupId: $vaultManagementGroupId
      }
    ) {
      vault @appendNode(connections: $connections, edgeTypeName: "VaultEdge") {
        id
        ...VaultCard_vault
        name
        vaultManagementGroup {
          id
        }
      }
    }
  }
`;

interface CreateVaultModalProps extends OverlayProps {
  vaultManager: CreateVaultModal_vaultManager$key;
  vaultsConnectionId: string;
}

export const MAX_AMOUNT_VAULT_MEMBERS = 16;

export const CreateVaultModal = ({
  vaultManager,
  vaultsConnectionId,
  ...props
}: CreateVaultModalProps) => {
  const isVaultManagementGroupsEnabled = useFeatureFlag(
    'FEATURE_VAULT_MANAGEMENT_GROUPS',
  );

  const { vaults, vaultManagementGroups, ...fragments } = useFragment(
    vaultManagerFragment,
    vaultManager,
  );

  const [createVault, isCreateVaultLoading] =
    useMutation<CreateVaultModal_createVault_Mutation>(createVaultMutation);

  const {
    isOpen: isConfirmationDialogOpen,
    onOpen: onConfirmationDialogOpen,
    onClose: onConfirmationDialogClose,
  } = useDisclosure();

  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [
    selectedVaultManagementGroupOption,
    setSelectedVaultManagementGroupOption,
  ] = useState<VaultManagementGroupOption | null>(null);

  const toast = useToast();

  const tryCreate = () => {
    const vaultExists = vaults.edges.some((edge) => {
      const members = edge.node?.members || [];
      return (
        members.length === memberIds.length &&
        members.every((member) => memberIds.includes(member.id))
      );
    });

    if (vaultExists) {
      onConfirmationDialogOpen();
    } else {
      onCreate();
    }
  };

  const onCreate = () => {
    createVault({
      variables: {
        memberIds,
        name,
        vaultManagementGroupId: selectedVaultManagementGroupOption?.id,
        connections: [vaultsConnectionId],
      },
      onCompleted: ({ createVault }) => {
        if (!createVault) {
          throw new Error('Expected new vault or existing vault');
        }

        const { vault } = createVault;
        if (vault) {
          toast({
            status: 'success',
            title: t`Created a new Vault`,
            description: (
              <Stack>
                <Text>{vault.name}</Text>
                <Button
                  as={Link}
                  colorScheme="whiteAlpha"
                  to={generatePath(VAULTS_MANAGER_VAULT_PATH, {
                    vaultId: vault.id,
                  })}
                >{t`View vault`}</Button>
              </Stack>
            ),
            isClosable: true,
          });
          props.onClose();
        }
      },
    });
  };

  const hasWrongNumberOfMembers =
    memberIds.length < 1 || memberIds.length > MAX_AMOUNT_VAULT_MEMBERS;

  const filteredVaultManagementGroups = vaultManagementGroups.edges
    .map(({ node }) => node)
    .filter(notEmpty);

  useEffect(() => {
    if (!props.isOpen) {
      setMemberIds([]);
      setName('');
    }
  }, [props.isOpen]);

  return (
    <>
      <Modal {...props}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t`Create shared vault`}</ModalHeader>
          <ModalBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>{t`Vault name`}</FormLabel>
                <Input
                  onChange={({ currentTarget }) => {
                    setName(currentTarget.value);
                  }}
                  type="text"
                ></Input>
              </FormControl>

              <FormControl>
                <Stack>
                  <FormLabel>{t`Vault members`}</FormLabel>
                  <FormHelperText
                    pb={2}
                  >{t`Select up to ${MAX_AMOUNT_VAULT_MEMBERS} people to share a vault.`}</FormHelperText>
                  <TenantMemberSelector
                    selectedMemberIds={memberIds}
                    allowSelectAll={false}
                    disableEmailInvite
                    onSelectedMembersUpdate={(memberIds) =>
                      setMemberIds(memberIds)
                    }
                    onSelectedEmailsUpdate={() => {}}
                    selectedEmails={[]}
                  />
                </Stack>
              </FormControl>

              {isVaultManagementGroupsEnabled && (
                <FormControl>
                  <Stack>
                    <FormLabel>{t`Assign Vault Management`}</FormLabel>

                    <VaultManagementGroupSelect
                      vaultManager={fragments}
                      onSelect={setSelectedVaultManagementGroupOption}
                      isDisabled={
                        filteredVaultManagementGroups.length === 1 &&
                        !!selectedVaultManagementGroupOption
                      }
                    />
                  </Stack>
                </FormControl>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter alignItems="end">
            <Button
              onClick={tryCreate}
              isDisabled={
                name.length === 0 ||
                hasWrongNumberOfMembers ||
                (isVaultManagementGroupsEnabled &&
                  !selectedVaultManagementGroupOption)
              }
              colorScheme="blue"
              isLoading={isCreateVaultLoading}
            >{t`Create`}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ConfirmationDialog
        action={{
          name: t`Vault with Same Members Already Exists`,
          onAction: () => {
            onConfirmationDialogClose();
            onCreate();
          },
        }}
        isOpen={isConfirmationDialogOpen}
        onClose={onConfirmationDialogClose}
      >
        {t` You're about to create a new vault with members that are already in an existing vault. Would you like to proceed?`}
      </ConfirmationDialog>
    </>
  );
};
